/**
 * The open project, owned by the dev server (or, in the browser version, by
 * the page itself): every file's current contents,
 * the command log and undo history, saving, and the rules for changes made
 * on disk by something else (an agent, a git pull, another editor).
 *
 * Browser tabs, the MCP server and the running-game bridge are all clients:
 * they send commands here and receive the resulting changes, so every client
 * sees the same state.
 */
import {
  applyOps,
  checkFile,
  COMPRESSED_MANIFEST,
  compressedEntries,
  compressStamp,
  themePathOf,
  soundsPathOf,
  fileKindFor,
  filesOf,
  findPrefabCycles,
  folderMoves,
  migrate,
  MoveError,
  normalizeProjectPath,
  isShaderPath,
  OpError,
  planMoves,
  relinkOps,
  stringifyCanonical,
  suggestRelinks,
  type FileMap,
  type FileMove,
  type Op,
  type PrefabFile,
  type Problem,
  type ProjectFile,
} from '@animo-editor/format';
import { Emitter } from './emitter.js';
import { fromBase64, hashBytes, hashText, toBase64, type Storage } from './storage.js';

export const PROJECT_FILE = 'project.json';

export interface FileMeta {
  /** Canonical text of what is on disk, or undefined when the file isn't on disk. */
  diskCanonical: string | undefined;
  /** Hash of the exact bytes last read from or written to disk. */
  diskHash: string | undefined;
  dirty: boolean;
  /** Text on disk that changed while this file had unsaved edits. */
  conflict?: string | undefined;
}

export interface HistoryEntry {
  id: number;
  label: string;
  ops: Op[];
  inverse: Op[];
  files: string[];
  /** Who sent it: a client id such as `tab:3` or `mcp`. */
  source: string;
  time: number;
  /** Files the command moved or renamed. */
  moves?: FileMove[];
  /** The ones of those moved in storage, which undo moves back: images and other files that aren't open JSON documents. */
  storageMoves?: FileMove[];
  /** Originals moved out of the root (`from`, a project path) into project.json's folder (`to`) to be compressed. Undo moves them back. */
  sourceMoves?: FileMove[];
  /** Compressed copies the command wrote. Undo removes them; the editor makes them again after a redo. */
  generated?: string[];
}

export type ChangeKind = 'apply' | 'undo' | 'redo' | 'reload' | 'resolve' | 'remove' | 'import';

export interface ChangeEvent {
  rev: number;
  kind: ChangeKind;
  label: string;
  source: string;
  /** The ops that changed the documents. For reloads, a createFile/deleteFile pair per file. */
  ops: Op[];
  dirty: string[];
  /** Files this change moved or renamed, so clients can follow them. Storage moves don't show in the ops. */
  moves?: FileMove[];
}

export class CommandRejected extends Error {
  constructor(message: string, readonly problems: Problem[] = []) {
    super(message);
    this.name = 'CommandRejected';
  }
}

export interface ProjectOptions {
  /** Save automatically this many milliseconds after the last edit. Off when omitted. */
  autosaveMs?: number;
  /** Most undo steps kept. Default 500. */
  historyLimit?: number;
}

export interface ProjectEvents {
  change: [ChangeEvent];
  saved: [{ files: string[]; rev: number }];
  conflict: [{ file: string }];
  history: [{ undo: string[]; redo: string[] }];
  error: [{ file?: string; message: string }];
  relinks: [{ relinks: FileMove[] }];
  /** Compressed copies the editor wrote again, outside the undo history. */
  generated: [{ files: string[] }];
  /** A shader file's text changed: written by an editor (`source` is its client), or on disk (`source` is "disk"). */
  text: [{ file: string; text: string; source: string }];
}

/** Turning a file into a compressed copy of an original kept outside the root. */
export interface CompressRequest {
  /** The file in the root that becomes the original. */
  file: string;
  /** Where the original goes, relative to project.json's folder. */
  source: string;
  /** The compressed copies, base64, in the root. */
  outputs: ImportedFile[];
}

const isDocPath = (p: string) => p.endsWith('.json');
const reversed = (moves: FileMove[]): FileMove[] => moves.map((m) => ({ from: m.to, to: m.from })).reverse();
const utf8 = (bytes: Uint8Array) => new TextDecoder().decode(bytes);

/** Files an import may add: images, atlas and Aseprite JSON, Aseprite sources kept beside their export, fonts, audio and video. */
export const IMPORT_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.svg', '.json', '.aseprite', '.ase', '.atlas', '.skel', '.woff2', '.woff', '.ttf', '.otf', '.xml', '.fnt', '.ogg', '.mp3', '.wav', '.m4a', '.flac', '.mp4', '.webm', '.ogv', '.m4v', '.mov'];
const MAX_IMPORT_BYTES = 32 * 1024 * 1024;

/** A binary file sent with an import, base64 encoded. */
export interface ImportedFile {
  path: string;
  data: string;
}

/** Where a project's files are: the folder holding project.json, and a way to reach the project's `root` folder. */
export interface ProjectSource {
  /** Storage for the folder that holds project.json. */
  projectStorage: Storage;
  /** File name of project.json in that folder. */
  projectName: string;
  /** Storage for the project's `root` folder, relative to project.json's folder. */
  openRoot(root: string): Storage;
}

export class EditorProject extends Emitter<ProjectEvents> {
  rev = 0;
  readonly docs: FileMap = new Map();
  readonly meta = new Map<string, FileMeta>();
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private nextEntryId = 1;
  private autosaveTimer: ReturnType<typeof setTimeout> | undefined;
  private queue: Promise<unknown> = Promise.resolve();
  /** Hashes of JSON files removed on disk by something else, to recognise them if they turn up under another name. */
  private removedHashes = new Map<string, string>();
  private relinkTimer: ReturnType<typeof setTimeout> | undefined;
  /**
   * Links that point at files moved outside the editor, each with the file
   * that seems to have taken its place. Clients offer to fix them.
   */
  relinks: FileMove[] = [];

  constructor(
    /** Storage for files under the project root. */
    readonly storage: Storage,
    /** Storage for the folder that holds project.json. */
    readonly projectStorage: Storage,
    /** File name of project.json. */
    readonly projectName: string,
    private options: ProjectOptions,
  ) {
    super();
  }

  /** Opens a project: reads project.json, then every JSON file under its root. */
  static async load<T extends EditorProject>(this: new (storage: Storage, projectStorage: Storage, projectName: string, options: ProjectOptions) => T, source: ProjectSource, options: ProjectOptions = {}): Promise<T> {
    const text = await source.projectStorage.read(source.projectName);
    if (text === undefined) throw new Error(`No project file ${source.projectName} in ${source.projectStorage.root}`);
    const project = migrate<ProjectFile>(JSON.parse(text)).file;
    const rootPath = (project.root ?? '.').replace(/^\.\/?/, '').replace(/\/$/, '');
    const root = rootPath ? source.openRoot(rootPath) : source.projectStorage;
    const p = new this(root, source.projectStorage, source.projectName, options);
    p.loadText(PROJECT_FILE, text);
    for (const file of await root.list(isDocPath)) {
      if (file === source.projectName && root === source.projectStorage) continue;
      const t = await root.read(file);
      if (t !== undefined) p.loadText(file, t);
    }
    // Files may have moved while the editor was closed.
    p.outsideChange(0);
    return p;
  }

  get project(): ProjectFile {
    return this.docs.get(PROJECT_FILE) as ProjectFile;
  }

  dirtyFiles(): string[] {
    return [...this.meta].filter(([, m]) => m.dirty).map(([f]) => f).sort();
  }

  history(): { undo: string[]; redo: string[] } {
    return { undo: this.undoStack.map((e) => e.label), redo: this.redoStack.map((e) => e.label).reverse() };
  }

  /** The step undo (or redo) would take next, without taking it. */
  peek(stack: 'undo' | 'redo'): Readonly<HistoryEntry> | undefined {
    return stack === 'undo' ? this.undoStack[this.undoStack.length - 1] : this.redoStack[this.redoStack.length - 1];
  }

  snapshot() {
    return {
      rev: this.rev,
      files: Object.fromEntries(this.docs),
      dirty: this.dirtyFiles(),
      conflicts: [...this.meta].filter(([, m]) => m.conflict !== undefined).map(([f]) => f),
      history: this.history(),
      relinks: this.relinks,
    };
  }

  /** Reads a file for a client: an open document's current contents, or raw text from disk. */
  async readRaw(file: string): Promise<string | undefined> {
    return this.storageFor(file).read(file);
  }

  /**
   * Applies one command: a labelled list of ops, undone as one step. Ops that
   * would leave a project, scene or prefab file invalid are refused.
   */
  apply(label: string, ops: Op[], source: string): ChangeEvent {
    this.checkOpPaths(ops);
    return this.record(label, ops, this.applyChecked(ops), source);
  }

  private checkOpPaths(ops: Op[]): void {
    if (!Array.isArray(ops) || ops.length === 0) throw new CommandRejected('A command needs at least one op');
    for (const op of ops) {
      try {
        if (normalizeProjectPath(op.file) !== op.file) throw new Error('not normalised');
      } catch {
        throw new CommandRejected(`Invalid file path "${op.file}"`);
      }
      if (!isDocPath(op.file)) throw new CommandRejected(`Only JSON files can be edited: ${op.file}`);
    }
  }

  /** Pushes an applied command onto the undo stack and announces it. */
  private record(label: string, ops: Op[], inverse: Op[], source: string, moved?: { moves: FileMove[]; storageMoves: FileMove[] }, extra?: Pick<HistoryEntry, 'sourceMoves' | 'generated'>): ChangeEvent {
    const files = [...filesOf(ops)];
    const entry: HistoryEntry = { id: this.nextEntryId++, label, ops, inverse, files, source, time: Date.now(), ...extra };
    if (moved?.moves.length) entry.moves = moved.moves;
    if (moved?.storageMoves.length) entry.storageMoves = moved.storageMoves;
    this.undoStack.push(entry);
    if (this.undoStack.length > (this.options.historyLimit ?? 500)) this.undoStack.shift();
    this.redoStack = [];
    return this.changed('apply', label, source, ops, files, entry.moves);
  }

  /** Hashes of the shader texts this project wrote, to tell its own writes from outside edits when the watcher sees them. */
  private writtenText = new Map<string, string>();

  private textPath(path: string): string {
    let normal: string;
    try {
      normal = normalizeProjectPath(String(path));
    } catch {
      throw new CommandRejected(`Invalid file path "${path}"`);
    }
    if (!isShaderPath(normal)) throw new CommandRejected(`${normal} is not a shader file (.frag)`);
    return normal;
  }

  /** A shader file's text, or undefined when there is none. */
  readText(path: string): Promise<string | undefined> {
    const file = this.textPath(path);
    return this.storage.read(file);
  }

  /**
   * Writes a shader file's text at once, outside the undo history: the
   * shader editor saves as you type, like a code editor. With `create`, the
   * file must not exist yet. Every client hears of it.
   */
  writeText(path: string, text: string, source: string, create = false): Promise<void> {
    return this.serial(async () => {
      const file = this.textPath(path);
      if (typeof text !== 'string') throw new CommandRejected('Shader text must be a string');
      if (create && (await this.storage.exists(file))) throw new CommandRejected(`${file} already exists`);
      this.writtenText.set(file, hashText(text));
      await this.storage.write(file, text);
      this.emit('text', { file, text, source });
    });
  }

  /** The watcher saw a shader file change: tells the clients, unless it was this project's own write. */
  async textChanged(path: string): Promise<void> {
    let file: string;
    try {
      file = this.textPath(path);
    } catch {
      return;
    }
    const text = await this.storage.read(file);
    if (text === undefined) return;
    if (this.writtenText.get(file) === hashText(text)) return;
    this.writtenText.delete(file);
    this.emit('text', { file, text, source: 'disk' });
  }

  /** Every file in the project except project.json: open JSON documents, and other files in storage. */
  async listFiles(): Promise<string[]> {
    const others = await this.storage.list((p) => !isDocPath(p));
    return [...new Set([...[...this.docs.keys()].filter((f) => f !== PROJECT_FILE), ...others])].sort();
  }

  /**
   * Renames or moves files and folders, rewriting every reference to them, as
   * one command. JSON documents move like any edit: the new file is written
   * and the old one removed on save. Other files (images) move in storage at
   * once, since the game and viewport load them by URL; undo moves them back.
   * Nothing is ever replaced.
   */
  moveFiles(label: string, moves: FileMove[], source: string): Promise<ChangeEvent> {
    return this.serial(async () => {
      if (!Array.isArray(moves) || !moves.length) throw new CommandRejected('A move needs at least one file');
      const all = await this.listFiles();
      const expanded: FileMove[] = [];
      for (const m of moves) {
        let from: string;
        try {
          from = normalizeProjectPath(String(m?.from));
        } catch {
          throw new CommandRejected(`Invalid file path "${m?.from}"`);
        }
        if (all.includes(from)) {
          expanded.push({ from, to: String(m.to) });
          continue;
        }
        // A folder: everything inside it moves.
        const inside = folderMoves(all, from, String(m.to).replace(/\/+$/, ''));
        if (!inside.length) throw new CommandRejected(`${from} does not exist`);
        expanded.push(...inside);
      }
      const taken = new Set<string>();
      for (const m of expanded) {
        let to: string;
        try {
          to = normalizeProjectPath(m.to);
        } catch {
          throw new CommandRejected(`Invalid file path "${m.to}"`);
        }
        // A JSON file with metadata but no document is deleted in memory, so its path is free.
        if (this.docs.has(to) || (isDocPath(to) && this.meta.has(to))) continue;
        if (await this.storage.exists(to)) taken.add(to);
      }
      let plan: ReturnType<typeof planMoves>;
      try {
        plan = planMoves(this.docs, expanded, (p) => taken.has(p));
      } catch (e) {
        if (e instanceof MoveError) throw new CommandRejected(e.message);
        throw e;
      }
      if (!plan.ops.length && !plan.storageMoves.length) throw new CommandRejected('Nothing to move');
      if (plan.ops.length) {
        // Dry-run the ops before moving anything in storage.
        this.checkOpPaths(plan.ops);
        applyOps(this.docs, this.applyChecked(plan.ops));
      }
      await this.moveInStorage(plan.storageMoves);
      try {
        // From here on there is no await, so no other command lands inside this one.
        const inverse = plan.ops.length ? this.applyChecked(plan.ops) : [];
        return this.record(label, plan.ops, inverse, source, { moves: plan.moves, storageMoves: plan.storageMoves });
      } catch (e) {
        await this.moveInStorage(reversed(plan.storageMoves)).catch(() => undefined);
        throw e;
      }
    });
  }

  /** Moves files in storage, all or none. */
  private async moveInStorage(moves: FileMove[]): Promise<void> {
    const done: FileMove[] = [];
    try {
      for (const m of moves) {
        await this.storage.move(m.from, m.to);
        done.push(m);
      }
    } catch (e) {
      for (const m of reversed(done)) await this.storage.move(m.from, m.to).catch(() => undefined);
      const failed = moves[done.length]!;
      throw new CommandRejected(`Could not move ${failed.from} to ${failed.to}: ${(e as Error).message}`);
    }
  }

  /**
   * Imports art: copies new files (images, atlas JSON) straight to disk, then
   * applies the ops that reference them (pack entries) as one command. The
   * files are on disk at once so the viewport and game can load them before
   * a save. Existing files are never replaced. Undo takes back the ops; the
   * copied files stay, unreferenced.
   */
  importFiles(label: string, files: ImportedFile[], ops: Op[], source: string): Promise<ChangeEvent> {
    return this.serial(async () => {
      if (!Array.isArray(files) || !files.length) throw new CommandRejected('An import needs at least one file');
      const decoded: { path: string; bytes: Uint8Array }[] = [];
      let total = 0;
      for (const f of files) {
        let normal: string;
        try {
          normal = normalizeProjectPath(String(f.path));
        } catch {
          throw new CommandRejected(`Invalid file path "${f.path}"`);
        }
        if (normal !== f.path) throw new CommandRejected(`Invalid file path "${f.path}"`);
        if (!IMPORT_EXTENSIONS.some((ext) => normal.toLowerCase().endsWith(ext))) {
          throw new CommandRejected(`Only images, JSON, Aseprite, Spine, font, audio and video files can be imported: ${normal}`);
        }
        if (normal === PROJECT_FILE) throw new CommandRejected(`${normal} can't be imported`);
        if (decoded.some((d) => d.path === normal)) throw new CommandRejected(`${normal} is in the import twice`);
        if (this.docs.has(normal) || (await this.storage.exists(normal))) throw new CommandRejected(`${normal} already exists`);
        let bytes: Uint8Array;
        try {
          bytes = fromBase64(String(f.data));
        } catch {
          throw new CommandRejected(`${normal} is not base64 data`);
        }
        if (isDocPath(normal)) {
          try {
            JSON.parse(utf8(bytes));
          } catch {
            throw new CommandRejected(`${normal} is not valid JSON`);
          }
        }
        total += bytes.length;
        if (total > MAX_IMPORT_BYTES) throw new CommandRejected('The import is over 32 MB');
        decoded.push({ path: normal, bytes });
      }
      // Dry-run the ops before writing anything, so a refused import leaves no files behind.
      this.checkOpPaths(ops);
      applyOps(this.docs, this.applyChecked(ops));
      const written: string[] = [];
      try {
        for (const d of decoded) {
          await this.storage.create(d.path, d.bytes);
          written.push(d.path);
        }
        // From here on there is no await, so no other command lands inside this one.
        const added = decoded.filter((d) => isDocPath(d.path)).map((d) => d.path);
        for (const file of added) this.loadText(file, utf8(decoded.find((d) => d.path === file)!.bytes));
        if (added.length) {
          const created: Op[] = added.map((file) => ({ op: 'createFile', file, value: this.docs.get(file) }));
          this.changed('import', `Imported ${added.join(', ')}`, source, created, added);
        }
        return this.record(label, ops, this.applyChecked(ops), source);
      } catch (e) {
        for (const p of written) await this.storage.remove(p).catch(() => undefined);
        if (e instanceof CommandRejected) throw e;
        throw new CommandRejected(`Could not write ${decoded[written.length]?.path}: ${(e as Error).message}`);
      }
    });
  }

  /** The root folder as a prefix of paths in project.json's folder: 'public/', or '' when the root is that folder. */
  private get rootPrefix(): string {
    const root = (this.project?.root ?? '.').replace(/^\.\/?/, '').replace(/\/$/, '');
    return root ? root + '/' : '';
  }

  /**
   * Compresses a file, keeping its original: moves the file out of the root
   * into project.json's folder (such as art/), writes the compressed copies,
   * and applies the ops that point the pack entry and every other reference
   * at them, as one command. Undo moves the original back and removes the
   * copies.
   */
  compressFile(label: string, req: CompressRequest, ops: Op[], source: string): Promise<ChangeEvent> {
    return this.serial(async () => {
      const norm = (p: unknown) => {
        try {
          const n = normalizeProjectPath(String(p));
          if (n === p) return n;
        } catch {
          // Refused below.
        }
        throw new CommandRejected(`Invalid file path "${String(p)}"`);
      };
      const file = norm(req?.file);
      const to = norm(req?.source);
      if (isDocPath(file) || !(await this.storage.exists(file))) throw new CommandRejected(`${file} is not a file to compress`);
      const prefix = this.rootPrefix;
      if (!prefix || to.startsWith(prefix) || to === this.projectName) throw new CommandRejected(`The original must go outside the folder the game serves, not ${to}`);
      if (await this.projectStorage.exists(to)) throw new CommandRejected(`${to} already exists`);
      if (!Array.isArray(req.outputs) || !req.outputs.length) throw new CommandRejected('Compressing needs the compressed copy');
      const outputs: { path: string; bytes: Uint8Array }[] = [];
      for (const o of req.outputs) {
        const path = norm(o.path);
        if (isDocPath(path) || !IMPORT_EXTENSIONS.some((ext) => path.toLowerCase().endsWith(ext))) throw new CommandRejected(`${path} can't be a compressed copy`);
        // The copy may take the original's name (an optimised PNG); anything else must be new.
        if (path !== file && (await this.storage.exists(path))) throw new CommandRejected(`${path} already exists`);
        outputs.push({ path, bytes: fromBase64(String(o.data)) });
      }
      this.checkOpPaths(ops);
      applyOps(this.docs, this.applyChecked(ops));
      const bytes = await this.storage.readBytes(file);
      if (!bytes) throw new CommandRejected(`Could not read ${file}`);
      const written: string[] = [];
      let moved = false;
      try {
        await this.projectStorage.create(to, bytes);
        await this.storage.remove(file);
        moved = true;
        for (const o of outputs) {
          await this.storage.replace(o.path, o.bytes);
          written.push(o.path);
        }
        // Stamped here, from the original where it now is, so the editor doesn't make the copies again.
        const state = await this.sourceState(to);
        const after = new Map(this.docs);
        applyOps(after, ops);
        const entries = compressedEntries(after);
        await this.setStamps(Object.fromEntries(outputs.map((o) => {
          const entry = entries.find((e) => e.compress.source === to && e.outputs.includes(o.path));
          return [o.path, entry ? compressStamp(entry, state) : ''];
        })));
      } catch (e) {
        for (const p of written) await this.storage.remove(p).catch(() => undefined);
        if (moved) await this.storage.create(file, bytes).catch(() => undefined);
        await this.projectStorage.remove(to).catch(() => undefined);
        throw new CommandRejected(`Could not compress ${file}: ${(e as Error).message}`);
      }
      // From here on there is no await, so no other command lands inside this one.
      return this.record(label, ops, this.applyChecked(ops), source, undefined, { sourceMoves: [{ from: file, to }], generated: outputs.map((o) => o.path) });
    });
  }

  /** An original (in project.json's folder) that some pack entry is compressed from, base64. */
  readSource(path: string): Promise<string> {
    return this.serial(async () => {
      if (!compressedEntries(this.docs).some((e) => e.compress.source === path)) throw new CommandRejected(`No pack entry is compressed from ${path}`);
      const bytes = await this.projectStorage.readBytes(path);
      if (!bytes) throw new CommandRejected(`${path} is missing`);
      return toBase64(bytes);
    });
  }

  /**
   * What the originals are now, to tell when one changed: size and change
   * time where the storage keeps them, else a hash. Also the manifest, which
   * says what each compressed copy was made from.
   */
  compressState(): Promise<{ sources: Record<string, string | null>; stamps: Record<string, string> }> {
    return this.serial(async () => {
      const sources: Record<string, string | null> = {};
      for (const e of compressedEntries(this.docs)) {
        const path = e.compress.source;
        if (!(path in sources)) sources[path] = await this.sourceState(path);
      }
      return { sources, stamps: await this.readStamps() };
    });
  }

  /** An original's state for stamps: size and change time where the storage keeps them, else a hash; null when it is missing. */
  private async sourceState(path: string): Promise<string | null> {
    const stat = await this.projectStorage.stat?.(path);
    if (stat) return `${stat.size}-${Math.round(stat.mtime)}`;
    const bytes = await this.projectStorage.readBytes(path);
    return bytes ? hashBytes(bytes) : null;
  }

  /** Writes compressed copies the editor made again, outside the undo history. Only copies some pack entry loads. */
  writeGenerated(files: ImportedFile[], stamps: Record<string, string>): Promise<string[]> {
    return this.serial(async () => {
      const allowed = new Set(compressedEntries(this.docs).flatMap((e) => e.outputs));
      const decoded = (Array.isArray(files) ? files : []).map((f) => {
        if (!allowed.has(String(f.path))) throw new CommandRejected(`${String(f.path)} is not a compressed copy any pack entry loads`);
        return { path: String(f.path), bytes: fromBase64(String(f.data)) };
      });
      for (const f of decoded) await this.storage.replace(f.path, f.bytes);
      // Copies no entry loads any more (the format changed) go, so they don't ship with the game.
      const stale = Object.keys(await this.readStamps()).filter((p) => !allowed.has(p));
      for (const p of stale) await this.storage.remove(p).catch(() => undefined);
      await this.setStamps({ ...Object.fromEntries(stale.map((p) => [p, ''])), ...Object.fromEntries(decoded.map((f) => [f.path, String(stamps?.[f.path] ?? '')])) });
      const paths = decoded.map((f) => f.path);
      if (paths.length) this.emit('generated', { files: paths });
      return paths;
    });
  }

  private async readStamps(): Promise<Record<string, string>> {
    try {
      const text = await this.projectStorage.read(COMPRESSED_MANIFEST);
      const value = text ? (JSON.parse(text) as unknown) : undefined;
      return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, string>) : {};
    } catch {
      return {};
    }
  }

  /** Merges stamps into the manifest; an empty stamp removes its copy's line. */
  private async setStamps(changes: Record<string, string>): Promise<void> {
    const stamps = await this.readStamps();
    for (const [path, stamp] of Object.entries(changes)) {
      if (stamp) stamps[path] = stamp;
      else delete stamps[path];
    }
    const sorted = Object.fromEntries(Object.entries(stamps).sort(([a], [b]) => a.localeCompare(b)));
    await this.projectStorage.write(COMPRESSED_MANIFEST, JSON.stringify(sorted, null, 2) + '\n');
  }

  private async moveToSource(m: FileMove): Promise<void> {
    const bytes = await this.storage.readBytes(m.from);
    if (!bytes) throw new CommandRejected(`${m.from} is missing`);
    await this.projectStorage.create(m.to, bytes);
    await this.storage.remove(m.from);
  }

  /** Takes a compress command's files back: the copies go, the originals return to the root. */
  private async uncompressStorage(entry: HistoryEntry): Promise<void> {
    // Every copy made from these originals goes, with copies no entry loads any more (an older format's), which may hold an original's name.
    const sources = new Set((entry.sourceMoves ?? []).map((m) => m.to));
    const loaded = new Set(compressedEntries(this.docs).filter((e) => !sources.has(e.compress.source)).flatMap((e) => e.outputs));
    const copies = new Set([...(entry.generated ?? []), ...Object.keys(await this.readStamps()).filter((p) => !loaded.has(p))]);
    for (const p of copies) await this.storage.remove(p).catch(() => undefined);
    await this.setStamps(Object.fromEntries([...copies].map((p) => [p, ''])));
    for (const m of entry.sourceMoves ?? []) {
      const bytes = await this.projectStorage.readBytes(m.to);
      if (!bytes) throw new CommandRejected(`The original ${m.to} is missing, so ${m.from} can't come back`);
      await this.storage.create(m.from, bytes);
      await this.projectStorage.remove(m.to);
    }
  }

  /** Undoes the last command. Runs after any storage work in progress, since a move's files go back too. */
  undo(source: string): Promise<ChangeEvent | undefined> {
    return this.serial(async () => {
      const entry = this.undoStack.pop();
      if (!entry) return undefined;
      const back = entry.storageMoves && reversed(entry.storageMoves);
      if (entry.sourceMoves) {
        try {
          await this.uncompressStorage(entry);
        } catch (e) {
          this.undoStack.push(entry);
          throw e;
        }
      }
      try {
        if (back) await this.moveInStorage(back);
        try {
          this.applyChecked(entry.inverse);
        } catch (e) {
          if (back) await this.moveInStorage(entry.storageMoves!).catch(() => undefined);
          throw e;
        }
      } catch (e) {
        this.undoStack.push(entry);
        throw e;
      }
      this.redoStack.push(entry);
      return this.changed('undo', entry.label, source, entry.inverse, entry.files, entry.moves && reversed(entry.moves));
    });
  }

  redo(source: string): Promise<ChangeEvent | undefined> {
    return this.serial(async () => {
      const entry = this.redoStack.pop();
      if (!entry) return undefined;
      try {
        if (entry.sourceMoves) for (const m of entry.sourceMoves) await this.moveToSource(m);
        if (entry.storageMoves) await this.moveInStorage(entry.storageMoves);
        try {
          entry.inverse = this.applyChecked(entry.ops);
        } catch (e) {
          if (entry.storageMoves) await this.moveInStorage(reversed(entry.storageMoves)).catch(() => undefined);
          throw e;
        }
      } catch (e) {
        this.redoStack.push(entry);
        throw e;
      }
      this.undoStack.push(entry);
      return this.changed('redo', entry.label, source, entry.ops, entry.files, entry.moves);
    });
  }

  /** Writes every changed file (or just `only`), in canonical form. */
  save(only?: string[]): Promise<string[]> {
    return this.serial(async () => {
      const targets = (only ?? this.dirtyFiles()).filter((f) => this.meta.get(f)?.dirty);
      for (const file of targets) {
        const meta = this.meta.get(file)!;
        const value = this.docs.get(file);
        const storage = this.storageFor(file);
        const name = file === PROJECT_FILE ? this.projectName : file;
        if (value === undefined) {
          meta.diskHash = undefined;
          meta.diskCanonical = undefined;
          await storage.remove(name);
          this.meta.delete(file);
        } else {
          const text = stringifyCanonical(value, fileKindFor(file));
          // Record the hash first so the watcher recognises our own write.
          meta.diskHash = hashText(text);
          meta.diskCanonical = text;
          await storage.write(name, text);
          // An edit may have landed while the write was in flight.
          const now = this.docs.get(file);
          meta.dirty = now === undefined || stringifyCanonical(now, fileKindFor(file)) !== text;
          meta.conflict = undefined;
        }
      }
      if (targets.length) this.emit('saved', { files: targets, rev: this.rev });
      return targets;
    });
  }

  /**
   * Handles a file that changed on disk. Our own writes are recognised by hash
   * and ignored. A file with no unsaved edits is reloaded and its entries are
   * dropped from undo history. A file with unsaved edits becomes a conflict
   * the user settles with `resolveConflict`; the two versions are never merged.
   */
  diskChanged(file: string): Promise<void> {
    return this.serial(async () => {
      if (!isDocPath(file)) return;
      const text = await this.storageFor(file).read(file === PROJECT_FILE ? this.projectName : file);
      const meta = this.meta.get(file);
      const hash = text === undefined ? undefined : hashText(text);
      if (meta && hash === meta.diskHash) return;
      if (!meta && text === undefined) return;
      if (meta?.dirty) {
        meta.conflict = text ?? '';
        this.emit('conflict', { file });
        return;
      }
      if (text === undefined && meta?.diskHash) this.removedHashes.set(file, meta.diskHash);
      this.reload(file, text, 'reload');
      this.outsideChange();
    });
  }

  /** Settles a conflict: keep the unsaved edits (the next save overwrites disk), or take the disk version. */
  resolveConflict(file: string, take: 'mine' | 'disk', source: string): Promise<ChangeEvent | undefined> {
    return this.serial(async () => {
      const meta = this.meta.get(file);
      if (!meta || meta.conflict === undefined) return undefined;
      const text = meta.conflict;
      meta.conflict = undefined;
      if (take === 'disk') return this.reload(file, text === '' ? await this.readRaw(file) : text, 'resolve', source);
      meta.diskHash = hashText(text);
      meta.diskCanonical = this.canonicalOf(file, text);
      meta.dirty = true;
      this.emit('history', this.history());
      return undefined;
    });
  }

  /**
   * Called when files change on disk by something other than the editor: an
   * agent, a script, git, a file manager. Soon after the last such change it
   * looks for links left pointing at files that moved, and announces them.
   */
  outsideChange(delayMs = 200): void {
    clearTimeout(this.relinkTimer);
    this.relinkTimer = setTimeout(() => void this.findRelinks().catch((e: Error) => this.emit('error', { message: e.message })), delayMs);
  }

  /** Works out `relinks` again, and announces it when it changed. */
  findRelinks(): Promise<FileMove[]> {
    return this.serial(async () => {
      const all = await this.listFiles();
      const removed = this.removedHashes;
      const next = suggestRelinks(this.docs, all, (missing, candidate) => {
        const hash = removed.get(missing);
        return hash !== undefined && this.meta.get(candidate)?.diskHash === hash;
      });
      if (JSON.stringify(next) !== JSON.stringify(this.relinks)) {
        this.relinks = next;
        this.emit('relinks', { relinks: next });
      }
      return next;
    });
  }

  /** Points links at the files that took their place (see `relinks`), as one undoable command. */
  relink(label: string, relinks: FileMove[], source: string): Promise<ChangeEvent> {
    return this.serial(async () => {
      if (!Array.isArray(relinks) || !relinks.length) throw new CommandRejected('Nothing to relink');
      const all = new Set(await this.listFiles());
      let ops: Op[];
      try {
        ops = relinkOps(this.docs, relinks.map((r) => ({ from: String(r?.from), to: String(r?.to) })), (p) => all.has(p)).ops;
      } catch (e) {
        if (e instanceof MoveError) throw new CommandRejected(e.message);
        throw e;
      }
      if (!ops.length) throw new CommandRejected('No links point at those files');
      this.checkOpPaths(ops);
      const event = this.record(label, ops, this.applyChecked(ops), source);
      this.outsideChange(0);
      return event;
    });
  }

  /** Cancels any pending autosave. */
  close(): void {
    clearTimeout(this.autosaveTimer);
    clearTimeout(this.relinkTimer);
  }

  private storageFor(file: string): Storage {
    return file === PROJECT_FILE ? this.projectStorage : this.storage;
  }

  /** Runs disk work one job at a time. */
  private serial<T>(job: () => Promise<T>): Promise<T> {
    const run = this.queue.then(job, job);
    this.queue = run.catch(() => undefined);
    return run;
  }

  private canonicalOf(file: string, text: string): string | undefined {
    try {
      return stringifyCanonical(JSON.parse(text), fileKindFor(file));
    } catch {
      return undefined;
    }
  }

  private loadText(file: string, text: string) {
    let value: unknown;
    try {
      value = JSON.parse(text);
    } catch (e) {
      this.emit('error', { file, message: `${file} is not valid JSON and was not opened: ${(e as Error).message}` });
      return false;
    }
    this.docs.set(file, value);
    this.meta.set(file, { diskHash: hashText(text), diskCanonical: stringifyCanonical(value, fileKindFor(file)), dirty: false });
    return true;
  }

  private reload(file: string, text: string | undefined, kind: ChangeKind, source = 'disk'): ChangeEvent | undefined {
    const ops: Op[] = [];
    if (this.docs.has(file)) ops.push({ op: 'deleteFile', file });
    if (text === undefined) {
      this.docs.delete(file);
      this.meta.delete(file);
    } else if (this.loadText(file, text)) {
      ops.push({ op: 'createFile', file, value: this.docs.get(file) });
    } else {
      return undefined;
    }
    // Undo steps that touch this file no longer apply to what is on disk.
    const keep = (e: HistoryEntry) => !e.files.includes(file);
    this.undoStack = this.undoStack.filter(keep);
    this.redoStack = this.redoStack.filter(keep);
    return this.changed(text === undefined ? 'remove' : kind, `Reloaded ${file}`, source, ops, [file]);
  }

  /** Applies ops, then refuses (and rolls back) if a touched file is left invalid. */
  private applyChecked(ops: Op[]): Op[] {
    let inverse: Op[];
    try {
      inverse = applyOps(this.docs, ops);
    } catch (e) {
      if (e instanceof OpError) throw new CommandRejected(e.message);
      throw e;
    }
    const problems: Problem[] = [];
    for (const file of filesOf(ops)) {
      if (!this.docs.has(file)) continue;
      problems.push(...checkFile(file, this.docs.get(file), themePathOf(this.project), soundsPathOf(this.project)));
      if (file.endsWith('.prefab.json')) {
        const getPrefab = (p: string) => this.docs.get(p) as PrefabFile | undefined;
        for (const cycle of findPrefabCycles(getPrefab, file)) {
          problems.push({ severity: 'error', file, message: `Prefab would contain itself: ${cycle.join(' → ')}` });
        }
      }
    }
    if (problems.length) {
      applyOps(this.docs, inverse);
      throw new CommandRejected(problems.map((p) => `${p.file}${p.at ? ` ${p.at}` : ''}: ${p.message}`).join('\n'), problems);
    }
    return inverse;
  }

  private changed(kind: ChangeKind, label: string, source: string, ops: Op[], files: string[], moves?: FileMove[]): ChangeEvent {
    for (const file of files) {
      const meta = this.meta.get(file);
      const value = this.docs.get(file);
      if (!meta) {
        if (value !== undefined) this.meta.set(file, { diskCanonical: undefined, diskHash: undefined, dirty: true });
        continue;
      }
      const canonical = value === undefined ? undefined : stringifyCanonical(value, fileKindFor(file));
      meta.dirty = canonical !== meta.diskCanonical;
      if (value === undefined && meta.diskCanonical === undefined) this.meta.delete(file);
    }
    this.rev++;
    const event: ChangeEvent = { rev: this.rev, kind, label, source, ops, dirty: this.dirtyFiles(), ...(moves?.length && { moves }) };
    this.emit('change', event);
    this.emit('history', this.history());
    this.scheduleAutosave();
    return event;
  }

  private scheduleAutosave() {
    if (!this.options.autosaveMs) return;
    clearTimeout(this.autosaveTimer);
    this.autosaveTimer = setTimeout(() => {
      this.save().catch((e: Error) => this.emit('error', { message: `Autosave failed: ${e.message}` }));
    }, this.options.autosaveMs);
  }
}
